import React, { useCallback, useEffect, useMemo, useState } from "react"
import { GetApp } from "../../App"
import { Native } from "../../App/Native"
import { GetShip } from "../../App/Context"
import { useEditState } from "../../App/useEditState"
import { BlockField, BlockRow, Block, BlockContainer } from "../../Components/Block"
import { ApiClient, DayApi, TripApi, ICruise, IDay, IDaysResponse, ITrip, ITripsResponse } from "../../Types"
import { Overlay, OverlayTitle } from "../../Components/Overlay"
import { GetPrintViewContext, PrintMode, TripListType, DayListType } from "./Context"
import { ShipDataType, GetPrintShipViewContext } from "../../App/Context"
import { GetShipView } from "../ShipView"

export const PrintSettingsViewData = {
  loadTrips(api: ApiClient, shipId: number, cruiseId: number): Promise<ITripsResponse> {
    return new TripApi(api).trips.list({ params: { shipId, cruiseId } }).call();
  },
  loadDays(api: ApiClient, shipId: number, tripId: number): Promise<IDaysResponse> {
    return new DayApi(api).days.list({ params: { shipId, tripId } }).call();
  },
  sortTrips(trips: ITrip[]): ITrip[] {
    return trips.toSorted((a, b) => a.id - b.id);
  },
  sortDays(days: IDay[]): IDay[] {
    return days.toSorted((a, b) => a.day - b.day);
  },
  initialTripId(sortedTrips: ITrip[], tripId?: number): number | undefined {
    if (sortedTrips.length === 0) {
      return undefined;
    }
    return tripId !== undefined ? tripId : sortedTrips[0].id;
  },
  parseTripId(value: string): number | undefined {
    const tripId = parseInt(value, 10);
    return isNaN(tripId) ? undefined : tripId;
  },
};

interface PrintSettingsViewProps {
  tripId?: number;
  onClose?: () => void;
}

export function PrintSettingsView({ tripId, onClose }: PrintSettingsViewProps) {
  const app = GetApp()!;
  const ship = GetShip();
  const context = GetPrintViewContext();
  const contextShip = GetPrintShipViewContext();
  const shipView = GetShipView();
  const t = app.getT("PrintSettingsView");
  const td = app.getT("DayView_tab");
  const tp = app.getT("PrintView");

  const { printRoot, printMode } = GetShipView();
  const [_edited, onEdited, _resetEdited] = useEditState();
  const [_trips, setTrips] = useState<ITrip[] | undefined>(undefined);
  const [selectedTripId, setSelectedTripId] = useState<number | undefined>(tripId);
  const [_days, setDays] = useState<IDay[] | undefined>(undefined);

  const printAction = useCallback(() => {
    Native.print(app);
  }, [app]);

  const cruise = useMemo(() => {
    return printRoot as ICruise | undefined;
  }, [printRoot]);

  useEffect(() => {
    const load = async () => {
      if (!cruise) {
        return;
      }
      const data = await PrintSettingsViewData.loadTrips(app.apiClient, ship.id, cruise.id);
      if (data.trips) {
        const sortedTrips = PrintSettingsViewData.sortTrips(data.trips);
        setTrips(sortedTrips);
        // Auto-select first trip if available
        const initial = PrintSettingsViewData.initialTripId(sortedTrips, tripId);
        if (initial !== undefined) {
          setSelectedTripId(initial);
        }
      }
    };
    load().catch(console.error);
  }, [cruise, ship]);

  // Load days when a trip is selected
  useEffect(() => {
    const loadDays = async () => {
      if (!selectedTripId) {
        setDays(undefined);
        return;
      }
      const data = await PrintSettingsViewData.loadDays(app.apiClient, ship.id, selectedTripId);
      if (data.days) {
        setDays(PrintSettingsViewData.sortDays(data.days));
      }
    };
    loadDays().catch(console.error);
  }, [selectedTripId, ship]);

  const _handleTripChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const tripId = PrintSettingsViewData.parseTripId(event.target.value);
    if (tripId !== undefined) {
      setSelectedTripId(tripId);
    }
    onEdited();
  }, [onEdited]);

  // const [settings, setSettings] = useState<IPrintSettings | undefined>(undefined);

  // const onEditEvent = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
  //   onEdited();
  // }, [onEdited]);

  // const save = useCallback(async (data: FormData, values: EditGroupData) => {
  //   await saveData(values);
  //   resetEdited();
  // }, []);

  const namePrefix = "printSettings";

  return (
    <div className="PrintSettingsView ">

      <Overlay className="" hideBackground={true}>
        <OverlayTitle title={t("title")} />

        <div className="Overlay-body Overlay-flex">

          {printMode === PrintMode.cruise && (
            <BlockContainer className="">

              <Block className="">
                <BlockRow>
                  <h3>{t("cruise")}</h3>
                </BlockRow>
                <BlockRow className="block-row-top-level-select">
                  {t("cruise_current")}
                </BlockRow>
              </Block>
            </BlockContainer>
          )}

          {printMode === PrintMode.trips && (
            <BlockContainer className="">
              <Block className="">
                <BlockRow>
                  <h3>{t("trips")}</h3>
                </BlockRow>
                <BlockRow className="block-row-checkboxes">
                  <BlockField label={t("trips_summary")} labelAfter={true} labelFor={`trips_summary`}>
                    <input type="checkbox" name={`${namePrefix}.trips_summary`} checked={context.includeTripData} onChange={() => context.setIncludeTripData(!context.includeTripData)} />
                  </BlockField>
                </BlockRow>

                <BlockRow className="block-row-radio-button">
                  <BlockField label={t("trips_summary_short")} labelAfter={true} labelFor={`trips_summary_short`} className="bigger">
                    <input type="radio" name={`${namePrefix}.trips_select`} checked={context.includeTripDayListType == TripListType.short} onChange={() => context.setIncludeTripDayListType(TripListType.short)} />
                  </BlockField>
                </BlockRow>
                <BlockRow className="block-row-radio-button block-row-radio-button-last">
                  <BlockField label={t("trips_summary_details")} labelAfter={true} labelFor={`trips_summary_details`} className="bigger">
                    <input type="radio" name={`${namePrefix}.trips_select`} checked={context.includeTripDayListType == TripListType.details} onChange={() => context.setIncludeTripDayListType(TripListType.details)} />
                  </BlockField>
                </BlockRow>

                <BlockRow className="block-row-checkboxes">
                  <BlockField label={t("journal_list")} labelAfter={true} labelFor={`journal`}>
                    <input type="checkbox" name={`${namePrefix}.journal`} checked={context.includeJournal} onChange={() => context.setIncludeJournal(!context.includeJournal)} />
                  </BlockField>
                </BlockRow>

                <BlockRow className="block-row-checkboxes">
                  <BlockField label={t("technical_list")} labelAfter={true} labelFor={`technical`}>
                    <input type="checkbox" name={`${namePrefix}.technical`} checked={context.includeTechnical} onChange={() => context.setIncludeTechnical(!context.includeTechnical)} />
                  </BlockField>
                </BlockRow>

              </Block>
            </BlockContainer>
          )}

          {printMode === PrintMode.days && (
            <BlockContainer className="">
              <Block className="print-settings-days width-2cols">
                <BlockRow>
                  <h3>{t("days")}</h3>
                </BlockRow>

                <BlockRow>
                  {t("days_text")}
                </BlockRow>

                <BlockRow className="block-row-checkboxes">
                  <BlockField label={td("track")} labelAfter={true} labelFor={`track`}>
                    <input type="checkbox" name={`${namePrefix}.track`} checked={context.includeDayTrack} onChange={() => context.setIncludeDayTrack(!context.includeDayTrack)} />
                  </BlockField>
                </BlockRow>

                <BlockRow className="block-row-checkboxes">
                  <BlockField label={td("weather_forecast")} labelAfter={true} labelFor={`weather_forecast`}>
                    <input type="checkbox" name={`${namePrefix}.weather_forecast`} checked={context.includeDayForecast} onChange={() => context.setIncludeDayForecast(!context.includeDayForecast)} />
                  </BlockField>
                </BlockRow>

                <BlockRow className="block-row-checkboxes">
                  <BlockField label={td("journal") + t("thumbnails")} labelAfter={true} labelFor={`journal`}>
                    <input type="checkbox" name={`${namePrefix}.journal`} checked={context.includeDayJournal} onChange={() => context.setIncludeDayJournal(!context.includeDayJournal)} />
                  </BlockField>
                </BlockRow>

                <BlockRow className="block-row-checkboxes">
                  <BlockField label={td("technical") + t("thumbnails")} labelAfter={true} labelFor={`technical`}>
                    <input type="checkbox" name={`${namePrefix}.technical`} checked={context.includeDayTechnical} onChange={() => context.setIncludeDayTechnical(!context.includeDayTechnical)} />
                  </BlockField>
                </BlockRow>

                <BlockRow className="block-row-checkboxes">
                  <BlockField label={td("watches")} labelAfter={true} labelFor={`watches`}>
                    <input type="checkbox" name={`${namePrefix}.watches`} checked={context.includeDayWatchplan} onChange={() => context.setIncludeDayWatchplan(!context.includeDayWatchplan)} />
                  </BlockField>
                </BlockRow>

                <BlockRow></BlockRow>

                <BlockRow className="block-row-checkboxes">
                  <BlockField label={t("entries")} labelAfter={true} labelFor={`entries`}>
                    <input type="checkbox" name={`${namePrefix}.entries`} checked={context.includeLogs} onChange={() => context.setIncludeLogs(!context.includeLogs)} />
                  </BlockField>
                </BlockRow>

                <BlockRow className="block-row-radio-button bigger">
                  <BlockField label={t("entries_summary")} labelAfter={true} labelFor={`entries_summary`}>
                    <input type="radio" name={`${namePrefix}.log_options`} checked={context.includeDayLogListType == DayListType.short} onChange={() => context.setIncludeDayLogListType(DayListType.short)} />
                  </BlockField>
                </BlockRow>
                <BlockRow className="block-row-radio-button bigger">
                  <BlockField label={t("entries_details")} labelAfter={true} labelFor={`entries_details`}>
                    <input type="radio" name={`${namePrefix}.log_options`} checked={context.includeDayLogListType == DayListType.details} onChange={() => context.setIncludeDayLogListType(DayListType.details)} />
                  </BlockField>
                </BlockRow>
                {context.includeDayLogListType == DayListType.details && (
                  <BlockRow className="block-row-checkboxes">
                    <BlockField label={t("edits")} labelAfter={true} labelFor={`edits`}>
                      <input type="checkbox" name={`${namePrefix}.edits`} checked={context.includeEdits} onChange={() => context.setIncludeEdits(!context.includeEdits)} />
                    </BlockField>
                  </BlockRow>
                )}
              </Block>
            </BlockContainer>
          )}

          {printMode === PrintMode.ship && (
            <BlockContainer className="">
              <Block className="print-settings-ship">
                <BlockRow>
                  <h3>{t("ship")}</h3>
                </BlockRow>
                <BlockRow className="block-row-radio-button">
                  <BlockField label={t("ship_short")} labelAfter={true} labelFor={`ship_short`} className="bigger">
                    <input type="radio" name={`${namePrefix}.ship_select`}
                      checked={contextShip.includeShipDataType == ShipDataType.short}
                      onChange={() => contextShip.setIncludeShipDataType(ShipDataType.short)}
                    />
                  </BlockField>
                </BlockRow>
                <BlockRow className="block-row-radio-button">
                  <BlockField label={t("ship_details")} labelAfter={true} labelFor={`ship_details`} className="bigger">
                    <input type="radio" name={`${namePrefix}.ship_select`}
                      checked={contextShip.includeShipDataType == ShipDataType.details}
                      onChange={() => contextShip.setIncludeShipDataType(ShipDataType.details)}
                    />
                  </BlockField>
                </BlockRow>

                <BlockRow></BlockRow>

                <BlockRow className="block-row-checkboxes">
                  <BlockField label={t("user_list")} labelAfter={true} labelFor={`user_list`}>
                    <input type="checkbox" name={`${namePrefix}.user_list`} checked={contextShip.includeUserList} onChange={() => contextShip.setIncludeUserList(!contextShip.includeUserList)} />
                  </BlockField>
                </BlockRow>
              </Block>
            </BlockContainer>
          )}

          <div className="button-row">
            <button className="button" onClick={() => { shipView.setPrinting(PrintMode.none, undefined); onClose?.(); }}>{tp("print_cancel")}</button>
            <button className="button default" onClick={() => printAction()}>{tp("print_action")}</button>
          </div>
        </div>
      </Overlay>
    </div>
  );
}
